/**
 * Shared IPv4 helpers. Consolidates the ad-hoc IP parsing/validation that was
 * previously copy-pasted across subscription modals.
 */

/** Strict IPv4 check — four decimal octets 0-255, no leading zeros / junk. */
export const isValidIPv4 = (ip: string): boolean => {
  const parts = (ip || '').trim().split('.');
  if (parts.length !== 4) return false;
  return parts.every((p) => {
    const n = parseInt(p, 10);
    return !Number.isNaN(n) && n >= 0 && n <= 255 && String(n) === p;
  });
};

/** Convert a valid IPv4 to its unsigned 32-bit number, or null if invalid. */
export const ipv4ToNumber = (ip: string): number | null => {
  if (!isValidIPv4(ip)) return null;
  const o = ip.trim().split('.').map((n) => parseInt(n, 10));
  return ((o[0] << 24) | (o[1] << 16) | (o[2] << 8) | o[3]) >>> 0;
};

/**
 * True if the two inclusive IPv4 ranges overlap. Returns false if any input is
 * unparseable (callers validate the individual fields separately).
 */
export const ipRangesOverlap = (s1: string, e1: string, s2: string, e2: string): boolean => {
  const a = ipv4ToNumber(s1);
  const b = ipv4ToNumber(e1);
  const c = ipv4ToNumber(s2);
  const d = ipv4ToNumber(e2);
  if (a === null || b === null || c === null || d === null) return false;
  return !(b < c || d < a);
};
